import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateLong } from "@/lib/dates";
import { StatusPill } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  CalendarDays, CalendarRange, FileClock, CheckCircle2, Clock, MapPin, PenLine,
  FileCheck2, RotateCcw, Briefcase, FileText, Receipt, Users, Building2, HardHat, ArrowRight, Wallet, AlertTriangle, TrendingUp, CalendarClock, MailCheck, ClipboardList,
} from "lucide-react";

// Config-driven dashboard cards: each defines destination route (+ filters), title and icon.
// Add a new widget by appending one entry here.
const CARDS = [
  { key: "jobs_today", label: "Jobs Today", icon: CalendarDays, tone: "bg-blue-100 text-blue-700", to: "/jobs?date=today" },
  { key: "jobs_this_week", label: "Jobs This Week", icon: CalendarRange, tone: "bg-indigo-100 text-indigo-700", to: "/jobs?range=week" },
  { key: "total_jobs", label: "Total Jobs", icon: Briefcase, tone: "bg-slate-100 text-slate-700", to: "/jobs" },
  { key: "awaiting_signature", label: "Awaiting Customer Signature", icon: PenLine, tone: "bg-yellow-100 text-yellow-800", to: "/jobs?view=awaiting_signature" },
  { key: "return_visits", label: "Return Visits", icon: RotateCcw, tone: "bg-orange-100 text-orange-700", to: "/jobs?view=return_visits" },
  { key: "signed_dockets", label: "Signed Dockets", icon: FileCheck2, tone: "bg-teal-100 text-teal-700", to: "/jobs?view=signed" },
  { key: "waiting_invoice", label: "Waiting to Invoice", icon: FileClock, tone: "bg-amber-100 text-amber-700", to: "/waiting-to-invoice" },
  { key: "completed_jobs", label: "Completed Jobs", icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700", to: "/jobs?status=completed" },
  { key: "pending_quotes", label: "Pending Quotes", icon: FileText, tone: "bg-sky-100 text-sky-700", to: "/quotes?status=pending" },
  { key: "outstanding_invoices", label: "Outstanding Invoices", icon: Receipt, tone: "bg-rose-100 text-rose-700", to: "/weekly-billing?filter=unpaid" },
  { key: "total_customers", label: "Customers", icon: Users, tone: "bg-violet-100 text-violet-700", to: "/customers" },
  { key: "total_builders", label: "Builders", icon: Building2, tone: "bg-cyan-100 text-cyan-700", to: "/customers?type=Builder" },
  { key: "pending_confirmations", label: "Job Confirmations", icon: MailCheck, tone: "bg-teal-100 text-teal-700", to: "/job-confirmations" },
  { key: "pending_leave", label: "Leave Requests", icon: CalendarClock, tone: "bg-purple-100 text-purple-700", to: "/leave" },
  { key: "total_employees", label: "Employees", icon: HardHat, tone: "bg-fuchsia-100 text-fuchsia-700", to: "/employees" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

function StatCard({ label, value, icon: Icon, tone, to }) {
  const navigate = useNavigate();
  const go = () => navigate(to);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${label}: ${value ?? 0}. Open ${label}.`}
      data-testid={`stat-${slug(label)}`}
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
      className="group bg-white rounded-2xl border border-slate-100 p-6 shadow-sm cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-navy/20 focus:outline-none focus:ring-2 focus:ring-navy/40"
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${tone}`}>
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-3xl font-heading font-bold text-navy" data-testid={`stat-value-${slug(label)}`}>{value ?? 0}</p>
      <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
        {label}
        <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
      </p>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [profit, setProfit] = useState(null);
  const [followOpen, setFollowOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/dashboard/profitability").then((r) => setProfit(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400">Loading dashboard…</div>;

  const money = (v) => `$${(Number(v) || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const followUp = data.follow_up_jobs || [];
  const noNotesCount = followUp.filter((j) => !j.has_notes).length;
  const profitCards = profit ? [
    { label: "Revenue This Month", value: money(profit.revenue), tone: "text-navy" },
    { label: "Gross Profit This Month", value: money(profit.gross_profit), tone: profit.gross_profit >= 0 ? "text-emerald-600" : "text-red-600" },
    { label: "Gross Margin %", value: `${(profit.margin || 0).toFixed(1)}%`, tone: profit.margin >= 30 ? "text-emerald-600" : profit.margin >= 15 ? "text-amber-600" : "text-red-600" },
    { label: "Avg Invoice Value", value: money(profit.avg_invoice_value), tone: "text-navy" },
    { label: "Jobs Completed", value: profit.jobs_completed, tone: "text-navy" },
    { label: "Outstanding Invoices", value: profit.outstanding_invoices, tone: "text-navy" },
    { label: "Outstanding Value", value: money(profit.outstanding_value), tone: "text-rose-600" },
  ] : [];

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Dashboard</h1>
          <p className="text-slate-500 mt-1">Operations overview for {formatDateLong(new Date())}</p>
        </div>
        <button data-testid="follow-up-btn" onClick={() => setFollowOpen(true)}
          className={`inline-flex items-center gap-2.5 rounded-xl px-4 py-3 font-semibold shadow-sm transition-all hover:-translate-y-0.5 shrink-0 ${followUp.length > 0 ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-white text-slate-600 border border-slate-200 hover:border-navy/20"}`}>
          <ClipboardList className="w-5 h-5" />
          <span className="text-left leading-tight">
            Jobs to Follow Up
            <span className="block text-xs font-normal opacity-90">{followUp.length} assigned &amp; not completed{noNotesCount > 0 ? ` · ${noNotesCount} with no notes` : ""}</span>
          </span>
          {followUp.length > 0 && <span className="ml-1 bg-white/25 rounded-full px-2 py-0.5 text-sm">{followUp.length}</span>}
        </button>
      </div>

      <Dialog open={followOpen} onOpenChange={setFollowOpen}>
        <DialogContent data-testid="follow-up-dialog" className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-500" />Jobs to Follow Up</DialogTitle>
            <DialogDescription>Assigned jobs due today or earlier that still aren't completed. Tap one to open the day and complete it as admin.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto -mx-2 px-2 divide-y divide-slate-100">
            {followUp.length === 0 && (
              <p data-testid="follow-up-empty" className="text-sm text-emerald-600 py-10 text-center">All caught up — no outstanding assigned jobs. 🎉</p>
            )}
            {followUp.map((j) => (
              <button key={j.id} data-testid={`follow-up-row-${j.id}`}
                onClick={() => { setFollowOpen(false); navigate(`/jobs/${j.id}?complete=${j.appointment_date}`); }}
                className="w-full text-left flex items-start gap-3 py-3.5 hover:bg-slate-50 transition-colors rounded-lg px-2">
                <div className="shrink-0 text-center w-16">
                  <span className={`block text-xs font-bold px-2 py-1 rounded-md ${j.days_overdue > 0 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                    {j.days_overdue > 0 ? `${j.days_overdue}d late` : "Today"}
                  </span>
                  <span className="block text-[11px] text-slate-400 mt-1">{j.appointment_date ? formatDateLong(new Date(j.appointment_date + "T00:00:00")).replace(/,.*/, "") : "—"}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-navy truncate">{j.customer_name || "Job"} <span className="text-slate-400 font-normal">· {j.job_number}</span></p>
                  <p className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{j.site_address || "No address"}</p>
                  <div className="flex items-center flex-wrap gap-1.5 mt-1">
                    <span className="text-xs text-slate-600 flex items-center gap-1"><HardHat className="w-3 h-3 shrink-0" />{j.workers && j.workers.length ? j.workers.join(", ") : "Unassigned"}</span>
                    {!j.has_notes && <span data-testid={`follow-up-no-notes-${j.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1"><PenLine className="w-3 h-3" />No notes yet</span>}
                    {j.reopened && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Re-opened</span>}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1 pt-0.5">
                  <StatusPill status={j.status} />
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {data.urgent_confirmations > 0 && (
        <button data-testid="urgent-confirmations-nudge" onClick={() => navigate("/job-confirmations")}
          className="w-full flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4 text-left hover:bg-amber-100 transition-colors">
          <span className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></span>
            <span>
              <span className="font-heading font-semibold text-amber-900 block">{data.urgent_confirmations} job{data.urgent_confirmations > 1 ? "s" : ""} start soon and haven't been confirmed</span>
              <span className="text-sm text-amber-700">Send the booking confirmation before the crew arrives on site.</span>
            </span>
          </span>
          <span className="text-sm font-semibold text-amber-900 flex items-center gap-1 shrink-0">Review now <ArrowRight className="w-4 h-4" /></span>
        </button>
      )}

      {data.attention_jobs && data.attention_jobs.length > 0 && (
        <div data-testid="attention-jobs-panel" className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 bg-amber-50 border-b border-amber-200 px-5 py-4">
            <span className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></span>
            <div>
              <h2 className="font-heading font-semibold text-amber-900">{data.attention_jobs.length} job{data.attention_jobs.length > 1 ? "s" : ""} not started or completed</h2>
              <p className="text-sm text-amber-700">Past jobs a worker hasn't finished on-site. Pay for that week is held until they're completed.</p>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {data.attention_jobs.map((j) => (
              <div key={j.id} data-testid={`attention-job-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-amber-50/50 cursor-pointer transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-navy truncate">{j.customer_name || "Job"} <span className="text-slate-400 font-normal">· {j.job_number}</span></p>
                  <p className="text-sm text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3.5 h-3.5 shrink-0" />{j.site_address || "No address"}</p>
                  {j.workers && j.workers.length > 0 && (
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><HardHat className="w-3.5 h-3.5 shrink-0" />{j.workers.join(", ")}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${j.state === "reopened" ? "bg-amber-100 text-amber-800" : j.state === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                    {j.state === "reopened" ? "Re-opened" : j.state === "in_progress" ? "Not completed" : "Not started"}
                  </span>
                  <p className="text-xs text-slate-400 mt-1">{formatDateLong(new Date(j.appointment_date + "T00:00:00"))}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {CARDS.map((c) => (
          <StatCard key={c.key} label={c.label} value={data[c.key]} icon={c.icon} tone={c.tone} to={c.to} />
        ))}
      </div>

      {profit && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="dashboard-profitability">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold text-navy flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />Financial Snapshot — {profit.month_label}
            </h2>
            <button data-testid="profit-view-cashflow" onClick={() => navigate("/reports/cashflow")}
              className="text-sm text-blue-700 font-medium flex items-center gap-1 hover:gap-1.5 transition-all">
              Weekly Cashflow <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            {profitCards.map((c, i) => (
              <div key={i} className="rounded-xl border border-slate-100 p-4" data-testid={`profit-card-${i}`}>
                <p className={`text-xl font-heading font-bold ${c.tone}`}>{c.value}</p>
                <p className="text-xs text-slate-500 mt-1">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.cashflow && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="dashboard-cashflow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold text-navy flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" />Cash Flow — Receivables</h2>
            <button data-testid="cashflow-view-ar" onClick={() => navigate("/invoices-awaiting-payment")}
              className="text-sm text-blue-700 font-medium flex items-center gap-1 hover:gap-1.5 transition-all">
              View Awaiting Payment <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-100 p-4" data-testid="cashflow-outstanding">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Total Outstanding</p>
              <p className="text-2xl font-heading font-bold text-navy mt-1">${(data.cashflow.total_outstanding || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            <div className="rounded-xl border border-slate-100 p-4" data-testid="cashflow-awaiting">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Awaiting Payment</p>
              <p className="text-2xl font-heading font-bold text-navy mt-1">{data.cashflow.awaiting_count}</p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-4" data-testid="cashflow-due-soon">
              <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold">Due Soon</p>
              <p className="text-2xl font-heading font-bold text-amber-700 mt-1">{data.cashflow.due_soon_count}</p>
            </div>
            <div className="rounded-xl border border-red-100 bg-red-50/40 p-4" data-testid="cashflow-overdue">
              <p className="text-xs uppercase tracking-wider text-red-600 font-semibold">Overdue</p>
              <p className="text-2xl font-heading font-bold text-red-700 mt-1">{data.cashflow.overdue_count}</p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-red-500" />Top Overdue Accounts</p>
            {(!data.cashflow.top_overdue || data.cashflow.top_overdue.length === 0) ? (
              <p className="text-sm text-emerald-600" data-testid="cashflow-no-overdue">No overdue invoices — receivables are healthy.</p>
            ) : (
              <div className="space-y-2" data-testid="cashflow-top-overdue">
                {data.cashflow.top_overdue.map((r, i) => (
                  <div key={i} onClick={() => navigate("/invoices-awaiting-payment")}
                    className="flex items-center justify-between rounded-lg border border-slate-100 p-3 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`cashflow-overdue-${i}`}>
                    <div>
                      <p className="text-sm font-medium text-navy">{r.customer || "—"} <span className="text-slate-400 font-normal">· {r.invoice_number}</span></p>
                      <p className="text-xs text-red-600 font-medium">{r.due_label}</p>
                    </div>
                    <span className="text-sm font-semibold text-navy">${(r.outstanding || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-semibold text-navy">Today's Jobs</h2>
            <button data-testid="dashboard-open-calendar" onClick={() => navigate("/calendar")}
              className="text-sm text-blue-700 font-medium flex items-center gap-1 hover:gap-1.5 transition-all">
              View Calendar <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {data.today_jobs.length === 0 && <p className="text-slate-400 text-sm py-8 text-center">No jobs scheduled for today.</p>}
          <div className="space-y-3">
            {data.today_jobs.map((j) => (
              <div key={j.id} data-testid={`dashboard-job-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}
                className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                <div className="flex items-center gap-1.5 text-navy font-semibold text-sm w-20 shrink-0">
                  <Clock className="w-4 h-4" /> {j.start_time || "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-navy truncate">{j.customer_name}</p>
                  <p className="text-sm text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.site_address || "No address"}</p>
                </div>
                <StatusPill status={j.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h2 className="text-xl font-heading font-semibold text-navy mb-4">Employee Schedule</h2>
          {data.employee_schedule.length === 0 && <p className="text-slate-400 text-sm">No active technicians.</p>}
          <div className="space-y-4">
            {data.employee_schedule.map((e, i) => (
              <div key={i} className="border-b border-slate-100 pb-3 last:border-0">
                <p className="font-semibold text-navy text-sm">{e.technician}</p>
                {e.jobs.length === 0
                  ? <p className="text-xs text-slate-400 mt-1">No jobs today</p>
                  : e.jobs.map((j) => (
                    <p key={j.id} className="text-xs text-slate-500 mt-1">{j.start_time} · {j.customer_name}</p>
                  ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
